import type { RefObject } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes, elevation } from "@similarweb/styles";
import { Tab, TabList } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import type { TeamListItem } from "app/pages/sales-intelligence/modules/teams/types/teams.types";
import type { TeamDetailTabHandle } from "app/pages/sales-intelligence/pages/management/types/teamDetail.types";

import { TeamFiltersContainer } from "../TeamFiltersContainer/TeamFiltersContainer";
import { TeamMembersContainer } from "../TeamMembersContainer/TeamMembersContainer";
import { TeamScoringContainer } from "../TeamScoringContainer/TeamScoringContainer";

import {
    StyledScrollableTeamDetailTabPanel,
    StyledTeamDetailTabPanel,
    StyledTeamDetailTabs,
} from "./styles";

type Props = {
    tabIndex: number;
    onSelectTab: (index: number) => void;
    teamId: number;
    team: TeamListItem;
    isNewTeam: boolean;
    hasVisitedFilters: boolean;
    hasVisitedScoring: boolean;
    filtersRef: RefObject<TeamDetailTabHandle | null>;
    scoringRef: RefObject<TeamDetailTabHandle | null>;
    onAddMembers: () => void;
};

export const TeamDetailTabsCard = ({
    tabIndex,
    onSelectTab,
    teamId,
    team,
    isNewTeam,
    hasVisitedFilters,
    hasVisitedScoring,
    filtersRef,
    scoringRef,
    onAddMembers,
}: Props) => {
    const { translate } = useTranslate();

    return (
        <Stack
            flex={1}
            minHeight="0"
            overflow="hidden"
            borderRadius="6px"
            boxShadow={elevation.Z1}
            background={colorsPalettes.carbon[0]}
            data-automation="team-detail-tabs-card"
        >
            <StyledTeamDetailTabs
                selectedIndex={tabIndex}
                onSelect={onSelectTab}
            >
                <TabList data-automation="team-detail-tabs">
                    <Tab data-automation="team-detail-tab-members">
                        <Text fontVariant="tab">{translate("si.management.teams.detail.tab.members")}</Text>
                    </Tab>
                    <Tab data-automation="team-detail-tab-filters">
                        <Text fontVariant="tab">{translate("si.management.teams.detail.tab.filters")}</Text>
                    </Tab>
                    <Tab data-automation="team-detail-tab-scoring">
                        <Text fontVariant="tab">{translate("si.management.teams.detail.tab.scoring")}</Text>
                    </Tab>
                </TabList>
                <StyledScrollableTeamDetailTabPanel data-automation="team-detail-panel-members">
                    <TeamMembersContainer
                        teamId={teamId}
                        members={team.users}
                        onAddMembers={onAddMembers}
                    />
                </StyledScrollableTeamDetailTabPanel>
                <StyledTeamDetailTabPanel
                    data-automation="team-detail-panel-filters"
                    forceRender={hasVisitedFilters}
                >
                    <TeamFiltersContainer
                        ref={filtersRef}
                        team={team}
                        isNewTeam={isNewTeam}
                    />
                </StyledTeamDetailTabPanel>
                <StyledTeamDetailTabPanel
                    data-automation="team-detail-panel-scoring"
                    forceRender={hasVisitedScoring}
                >
                    <TeamScoringContainer
                        ref={scoringRef}
                        team={team}
                    />
                </StyledTeamDetailTabPanel>
            </StyledTeamDetailTabs>
        </Stack>
    );
};
